import { useCallback, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { applyInvalidations, itemReactionsPatch, userTopicInvalidations } from '@tradeoutlet/data';
import { RealtimeEvents, lastMessageOf, topics, typingKey, type Conversation, type Message, type ReactionSummary, type WorkspaceMember } from '@tradeoutlet/shared';
import { api, recheckSession } from './api';
import { useAuth } from './auth';
import { useConversations, useWorkspaceId } from './hooks';
import { messageStore } from './messageStore';
import { qk, queryClient } from './queryClient';
import { connection } from './connection';
import { realtime } from './realtime';
import { useUi } from './store';

/** Calls we've already shown an incoming-call prompt for (polling fallback must not re-ring). */
const promptedCalls = new Set<string>();

/** t253u: while the app is on screen, ask for ringing calls this often (a phone's socket can miss the invite). */
export const INCOMING_CALL_CHECK_MS = 15_000;

/** Which conversation is on screen (unread counts are not bumped for it). */
export const activeConversation = { id: null as string | null };

const sessionId = crypto.randomUUID();

/** Names for the list preview from what is already cached (conversation members, then the workspace directory). */
function cachedName(ws: string, c: Conversation, userId: string): string | null {
  return c.members?.find(x => x.user_id === userId)?.display_name
    ?? queryClient.getQueryData<{ members: WorkspaceMember[] }>(qk.members(ws))?.members.find(x => x.user_id === userId)?.display_name ?? null;
}

/** Newest item.reactions event time per item, so an older event arriving late is ignored. */
const reactionTimes = new Map<string, string>();

function patchConversation(ws: string, id: string, fn: (c: Conversation) => Conversation) {
  queryClient.setQueryData<{ conversations: Conversation[] }>(qk.conversations(ws), old =>
    old ? { conversations: old.conversations.map(c => (c.id === id ? fn(c) : c)) } : old);
}

/**
 * Wires Supabase Realtime (or polling in local dev) into the caches:
 *  - one private topic per conversation the user belongs to + the user's own topic
 *  - after every (re)subscribe, after_seq catch-up for loaded conversations
 *  - presence heartbeat + typing expiry
 */
export function RealtimeBridge() {
  const { userId, getAccessToken } = useAuth();
  const ws = useWorkspaceId();
  const convs = useConversations();
  const setPresence = useUi(s => s.setPresence);
  const setTyping = useUi(s => s.setTyping);
  const pruneTyping = useUi(s => s.pruneTyping);
  const clearTyping = useUi(s => s.clearTyping);
  const setIncomingCall = useUi(s => s.setIncomingCall);
  const toast = useUi(s => s.toast);
  const refetchTimer = useRef<number | null>(null);
  const { search, pathname } = useLocation();
  const navigate = useNavigate();

  /**
   * t253u: show a call that is ringing for me but whose invite never arrived on the socket. Phones drop the socket
   * when the app is in the background or the screen locks, and broadcasts sent meanwhile are lost, so this runs when
   * the user topic (re)subscribes, when the app comes back on screen, every INCOMING_CALL_CHECK_MS while visible, and
   * when a call push is tapped (?call=<id>, which also re-shows a call prompted earlier in this session).
   */
  const checkIncoming = useCallback((forceId?: string) => {
    if (!ws || useUi.getState().incomingCall || useUi.getState().activeCall) return;
    void Promise.resolve().then(() => api.incomingCalls()).then(({ calls: list }) => {
      const c = list.find(x => x.workspace_id === ws && (x.id === forceId || !promptedCalls.has(x.id)));
      if (!c || useUi.getState().incomingCall || useUi.getState().activeCall) return;
      promptedCalls.add(c.id);
      setIncomingCall({ call_id: c.id, media: c.media, conversation_id: c.conversation_id, from_user_id: c.started_by, from_name: c.from_name ?? 'A colleague', received_at: Date.now() });
    }).catch(() => undefined);
  }, [ws, setIncomingCall]);

  // Auth for Realtime private channels
  useEffect(() => { void getAccessToken().then(t => realtime.setAuth(t)); }, [getAccessToken, userId]);

  // Presence heartbeat (Redis TTL on the server) + periodic presence snapshot
  useQuery({
    queryKey: qk.presence(ws ?? ''),
    enabled: Boolean(ws),
    refetchInterval: 30_000,
    queryFn: async () => {
      await api.heartbeat({ workspace_id: ws!, session_id: sessionId, status: document.hidden ? 'away' : 'online' }).catch(() => undefined);
      const r = await api.presence(ws!);
      setPresence(r.presence);
      return r;
    },
  });

  useEffect(() => {
    const t = window.setInterval(pruneTyping, 1500);
    return () => window.clearInterval(t);
  }, [pruneTyping]);

  // User topic: notifications, call invites, read-state sync, workforce updates
  useEffect(() => {
    if (!userId || !ws) return;
    return realtime.subscribe(topics.user(userId), (event, payload) => {
      // Cache refreshes are shared with the native app (packages/data); side effects below are this app's.
      // t162: notification.read arrives when read on another tab / device, or by interacting with the item.
      applyInvalidations(queryClient, userTopicInvalidations(ws, event, payload));
      // t187u: someone reacted on an item I can see: new counts and who-reacted, no refetch.
      const rx = itemReactionsPatch(ws, event, payload, reactionTimes);
      if (rx) queryClient.setQueryData(rx.key, rx.reactions);
      switch (event) {
        case RealtimeEvents.MemberRemoved:
          // t449u: an owner deleted the channel I'm looking at: leave it for the chat list.
          if (payload.deleted && payload.conversation_id && activeConversation.id === payload.conversation_id) {
            queryClient.removeQueries({ queryKey: qk.conversation(String(payload.conversation_id)) });
            toast('This channel was deleted', 'info');
            navigate('/chat', { replace: true });
          }
          break;
        case RealtimeEvents.ChannelVisibility:
          // t722u: a channel I'm not in became private while I was looking at it: back to the chat list.
          if (payload.is_private && payload.conversation_id && activeConversation.id === payload.conversation_id) {
            queryClient.removeQueries({ queryKey: qk.conversation(String(payload.conversation_id)) });
            toast('This channel is now private', 'info');
            navigate('/chat', { replace: true });
          }
          break;
        case RealtimeEvents.SessionRevoked:
          // t363u: sessions were ended elsewhere; if this was one of them the refresh fails and we land on sign in.
          recheckSession();
          break;
        case RealtimeEvents.Notification:
          // t145: a shift offered to you (or an answer to yours) pops up straight away.
          if (['workforce.shift_offer', 'workforce.shift_offer_response'].includes(String(payload.kind)) && payload.title) toast(String(payload.title), 'info');
          break;
        case RealtimeEvents.CallInvite: {
          const call = payload.call as { id: string; media: 'audio' | 'video'; conversation_id: string | null };
          const from = payload.from as { user_id: string; display_name?: string };
          promptedCalls.add(call.id);
          setIncomingCall({ call_id: call.id, media: call.media, conversation_id: call.conversation_id, from_user_id: from.user_id, from_name: from.display_name ?? 'A colleague', received_at: Date.now() });
          break;
        }
        case RealtimeEvents.CallUpdated: {
          const cur = useUi.getState().incomingCall;
          const mine = (payload.participants as { user_id: string; status: string }[] | undefined)?.find(p => p.user_id === userId);
          // Over, or answered / declined on another of my devices.
          if (cur && cur.call_id === payload.id && (payload.status === 'ended' || (mine && mine.status !== 'invited'))) setIncomingCall(null);
          break;
        }
        case RealtimeEvents.ReadState:
          patchConversation(ws, String(payload.conversation_id), c => ({ ...c, last_read_sequence: Number(payload.last_read_sequence), unread_count: 0, mention_count: 0 }));
          break;
        case RealtimeEvents.ShiftsPublished:
          toast('A new rota has been published', 'info');
          break;
      }
    });
  }, [userId, ws, setIncomingCall, toast, navigate]);

  // One topic per conversation
  const convIds = (convs.data?.conversations ?? []).map(c => `${c.kind}:${c.id}`).sort().join(',');
  useEffect(() => {
    if (!ws || !userId || !convIds) return;
    const subs = convIds.split(',').map(entry => {
      const [kind, id] = entry.split(':') as [Conversation['kind'], string];
      return realtime.subscribe(topics.conversation(kind, id), (event, payload) => {
        switch (event) {
          case RealtimeEvents.MessageCreated: {
            const m = payload as unknown as Message;
            messageStore.ingest(id, [m]);
            clearTyping(typingKey(id, m.parent_id), m.sender_id); // t657u: their message is here, so they've stopped
            const isActive = activeConversation.id === id && !document.hidden;
            patchConversation(ws, id, c => ({
              ...c,
              last_sequence: Math.max(c.last_sequence, m.sequence),
              last_message_at: m.created_at,
              // Quiet messages (system users such as the deploy smoke test) never count as unread.
              unread_count: !isActive && !m.parent_id && m.sender_id !== userId && m.kind === 'user' && !m.metadata?.quiet ? c.unread_count + 1 : c.unread_count,
              mention_count: !isActive && m.mentions?.includes(userId) && !m.metadata?.quiet ? c.mention_count + 1 : c.mention_count,
              last_message: lastMessageOf(m, u => cachedName(ws, c, u)) ?? c.last_message,
            }));
            break;
          }
          case RealtimeEvents.MessageUpdated:
          case RealtimeEvents.MessageDeleted:
            messageStore.applyUpdate(id, payload as unknown as Message);
            if (event === RealtimeEvents.MessageDeleted) messageStore.applyUpdate(id, { id: String(payload.id), body: '', deleted_at: String(payload.deleted_at) });
            // The list preview was this message (edited or deleted): ask the server for the new one.
            if (queryClient.getQueryData<{ conversations: Conversation[] }>(qk.conversations(ws))?.conversations.find(c => c.id === id)?.last_message?.id === String(payload.id)) {
              void queryClient.invalidateQueries({ queryKey: qk.conversations(ws) });
            }
            break;
          case RealtimeEvents.ReactionsChanged:
            messageStore.applyReactions(id, String(payload.message_id), payload.reactions as ReactionSummary[]);
            break;
          case RealtimeEvents.PinsChanged:
            messageStore.applyUpdate(id, { id: String(payload.message_id), pinned: Boolean(payload.pinned) });
            void queryClient.invalidateQueries({ queryKey: qk.pins(id) });
            break;
          case RealtimeEvents.AckUpdated:
            void queryClient.invalidateQueries({ queryKey: ['ack', String(payload.message_id)] });
            void queryClient.invalidateQueries({ queryKey: ['acks'] });
            break;
          case RealtimeEvents.PollUpdated:
            void queryClient.invalidateQueries({ queryKey: ['poll', String(payload.poll_id)] });
            break;
          case RealtimeEvents.TaskLinksChanged:
            void queryClient.invalidateQueries({ queryKey: qk.taskLinks(id) });
            break;
          case RealtimeEvents.Typing:
            // t657u: per thread when typing a reply, so the thread shows it and the channel doesn't.
            if (payload.user_id !== userId) setTyping(typingKey(id, (payload.parent_id as string | null | undefined) ?? null), String(payload.user_id));
            break;
          case RealtimeEvents.ConversationUpdated:
          case RealtimeEvents.MemberAdded:
            void queryClient.invalidateQueries({ queryKey: qk.conversations(ws) });
            void queryClient.invalidateQueries({ queryKey: qk.conversation(id) });
            void queryClient.invalidateQueries({ queryKey: ['conversation-readers', id] });
            void queryClient.invalidateQueries({ queryKey: ['channel-access', id] });
            void queryClient.invalidateQueries({ queryKey: ['channel-form', id] });
            if (payload.permissions_changed) void queryClient.invalidateQueries({ queryKey: ['channel-permissions', id] }); // t520u
            break;
        }
      });
    });
    return () => subs.forEach(u => u());
  }, [convIds, ws, userId, setTyping, clearTyping]);

  // Unsent messages kept on this device (IndexedDB): show them again after a reload and resend them.
  useEffect(() => { if (userId) void messageStore.restoreUnsent(userId, { resend: connection.isDeviceOnline() }); }, [userId]);

  // After any (re)subscribe: recover anything missed while disconnected
  useEffect(() => {
    if (!ws) return;
    return realtime.onSubscribed(topic => {
      const m = /^collab:chat:(?:channel|dm):(.+)$/.exec(topic);
      if (m) void messageStore.catchUp(m[1]);
      void messageStore.retryUnsent();
      if (refetchTimer.current) window.clearTimeout(refetchTimer.current);
      refetchTimer.current = window.setTimeout(() => {
        void queryClient.invalidateQueries({ queryKey: qk.conversations(ws) });
        void queryClient.invalidateQueries({ queryKey: qk.notifications(ws) });
      }, 1000);
    });
  }, [ws]);

  // Local-dev fallback (no Supabase Realtime): poll with after_seq
  useEffect(() => {
    if (realtime.mode !== 'polling' || !ws) return;
    const fast = window.setInterval(() => { if (activeConversation.id) void messageStore.catchUp(activeConversation.id); }, 3000);
    const slow = window.setInterval(() => {
      void queryClient.invalidateQueries({ queryKey: qk.conversations(ws) });
      void queryClient.invalidateQueries({ queryKey: qk.notifications(ws) });
    }, 15000);
    // Incoming calls normally arrive on the user topic; without Realtime poll for ringing calls.
    const calls = window.setInterval(() => checkIncoming(), 4000);
    return () => { window.clearInterval(fast); window.clearInterval(slow); window.clearInterval(calls); };
  }, [ws, checkIncoming]);

  // t253u: ringing calls the socket missed (see checkIncoming).
  useEffect(() => {
    if (!ws || !userId) return;
    checkIncoming();
    const me = topics.user(userId);
    const offSub = realtime.onSubscribed(topic => { if (topic === me) checkIncoming(); });
    const onVisible = () => { if (!document.hidden) checkIncoming(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    window.addEventListener('pageshow', onVisible);
    const t = window.setInterval(onVisible, INCOMING_CALL_CHECK_MS);
    return () => {
      offSub(); window.clearInterval(t);
      document.removeEventListener('visibilitychange', onVisible); window.removeEventListener('focus', onVisible); window.removeEventListener('pageshow', onVisible);
    };
  }, [ws, userId, checkIncoming]);

  // t253u: a tapped call push opens /…?call=<id>: ring that call here, then drop the parameter.
  useEffect(() => {
    const id = new URLSearchParams(search).get('call');
    if (!id || !ws) return;
    checkIncoming(id);
    const rest = new URLSearchParams(search); rest.delete('call');
    navigate({ pathname, search: rest.toString() ? `?${rest}` : '' }, { replace: true });
  }, [search, pathname, ws, checkIncoming, navigate]);

  // t574u: back on screen / online / woken up: reconnect now rather than after the socket's backoff.
  useEffect(() => {
    const onWake = () => { if (!document.hidden) realtime.nudge(); };
    document.addEventListener('visibilitychange', onWake);
    window.addEventListener('focus', onWake);
    window.addEventListener('pageshow', onWake);
    window.addEventListener('online', onWake);
    // Rejoin channels stuck errored even if nobody touches the tab.
    const t = window.setInterval(onWake, 15_000);
    return () => {
      window.clearInterval(t);
      document.removeEventListener('visibilitychange', onWake); window.removeEventListener('focus', onWake); window.removeEventListener('pageshow', onWake);
      window.removeEventListener('online', onWake);
    };
  }, []);

  // t574u: live updates came back after a drop: refetch what's on screen (the per-topic catch-up above covers chat).
  useEffect(() => realtime.onRecovered(() => {
    if (activeConversation.id) void messageStore.catchUp(activeConversation.id);
    void queryClient.invalidateQueries({ refetchType: 'active' });
  }), []);

  // Coming back to the tab: catch up the visible conversation immediately
  useEffect(() => {
    const onVis = () => { if (!document.hidden && activeConversation.id) void messageStore.catchUp(activeConversation.id); };
    document.addEventListener('visibilitychange', onVis);
    const onOnline = () => { onVis(); void messageStore.retryUnsent(); };
    window.addEventListener('online', onOnline);
    return () => { document.removeEventListener('visibilitychange', onVis); window.removeEventListener('online', onOnline); };
  }, []);

  return null;
}
